import * as Sentry from '@sentry/react';
import apiService from '@/store/apiService';
import { store } from '@/store/store';
import apiFetch, { removeGlobalHeaders, setGlobalHeaders, setSigningOut } from '@/utils/apiFetch';
import { clearAllFailures } from '@/utils/configHook/failure';
import { getLocalStorage } from '@/utils/localStorage';
import {
	authForgotPassword,
	authOtpSignIn,
	authRefreshToken,
	authResetKnownPassword,
	authResetPassword,
	authSignIn,
	authSignInSocial,
	authSignInWithToken,
	authSignUp,
	authSignUpSocial,
	authSocialsOtp,
	fetchProfilePicture,
	fetchUser,
} from '@auth/authApi';
import JwtAuthContext, { JwtAuthContextType } from '@auth/services/jwt/JwtAuthContext';
import { FuseAuthProviderComponentProps, FuseAuthProviderState } from '@fuse/core/FuseAuthProvider/types/FuseAuthTypes';
import useLocalStorage from '@fuse/hooks/useLocalStorage';
import jwtDecode from 'jwt-decode';
import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { DNSUser, Role, User } from '../../user';
import { convertImageToBase64, isTokenValid } from './utils/jwtUtils';

/**
 * Standard authentication session response
 */
type AuthSession = {
	refresh: string;
	token: string;
	create_date: string;
	expiry_date: string;
	token_type: string;
};

/**
 * Social authentication session response (uses 'access' instead of 'token')
 */
type SocialAuthSession = Omit<AuthSession, 'token'> & {
	access: string;
	remember_me_token?: string;
	message?: string;
};

/**
 * Decoded JWT token structure
 */
type DecodedToken = {
	token_type: string;
	exp: number;
	iat: number;
	jti: string;
	user_id: number;
	epp_id: string;
	roles: string[];
	username: string;
	is_2fa_enabled: boolean;
};

/**
 * Payload for standard username/password authentication
 *
 * @property username - User's login identifier (typically email or username)
 * @property password - User's password for authentication
 * @property remember_me_token - Optional token for persistent authentication sessions
 */
export type JwtSignInPayload = {
	username: string;
	password: string;
	remember_me_token?: string;
};

/**
 * Payload for creating a new user account
 *
 * @property displayName - User's full name or display name
 * @property email - User's email address for identification and communication
 * @property password - User's chosen password for securing their account
 */
export type JwtSignUpPayload = {
	first: string;
	last: string;
	email: string;
	passwd: string;
	tel: string;
	rar: string;
	jobtitle: string;
	app_url: string;

};

/**
 * Payload for two-factor authentication with OTP
 *
 * @property username - User's login identifier
 * @property password - User's password
 * @property otp - One-time password code from authenticator app or SMS
 * @property remember_me_duration - Optional duration in days to remember this device
 *                                  (to potentially bypass OTP on future logins)
 */
export type JwtOTPSignInPayload = {
	username: string;
	password: string;
	otp?: string;
	remember_me_duration?: number;
	recovery_code?: string;
	method?: string;  // For specifying alternative 2FA method
};

/**
 * Payload for initiating password recovery process
 *
 * @property email - User's registered email where reset instructions will be sent
 * @property app_url - Application URL used to construct the password reset link
 */
export type JwtForgotPasswordPayload = {
	email: string;
	app_url: string;
};

/**
 * Payload for setting a new password using a reset token
 *
 * @property password - User's new password
 * @property confirm_password - Confirmation of new password (must match password)
 * @property token - Security token received via email to authorize the password reset
 */
export type JwtResetPasswordPayload = {
	password: string;
	confirm_password: string;
	token: string;
};

/**
 * Payload for changing password when current password is known
 *
 * @property old_password - User's current password for verification
 * @property new_password - User's desired new password
 * @property confirm_password - Confirmation of new password (must match new_password)
 */
export type JwtResetPasswordKnownPayload = {
	old_password: string;
	new_password: string;
	confirm_password: string;
};

export type JwtSignInSocials = {
	token: string;
	provider: string;
	local?: string;
};

export type JwtSignUpSocials = {
	token: string;
	provider: string;
	local?: string;
	first: string,
	last: string,
	tel: string,
	rar: string,
	jobtitle: string,
	passwd: string,
	app_url: string;
};

export type JwtSocialsOtp = {
	identifier: string;
	'2fa_type': string;
	otp?: string;
	remember_me_duration?: number;
	recovery_code?: string;
};

/**
 * Transforms a social auth session response to standard session format
 */
function transformSocialSession(session: SocialAuthSession): AuthSession {
	return {
		refresh: session.refresh,
		token: session.access,
		create_date: session.create_date,
		expiry_date: session.expiry_date,
		token_type: session.token_type
	};
}

/**
 * Fetches and constructs a complete user profile with all necessary data
 */
async function buildUserProfile(token: string, decoded: DecodedToken): Promise<User> {
	const mappedRoles = decoded?.roles?.map(role => role as Role) || [Role.Primary];

	const profileDetails = await fetchUser({ token });
	const userData = (await profileDetails.json()) as DNSUser;

	let photoUrl: string;
	try {
		const profileImage = await fetchProfilePicture({});
		photoUrl = await convertImageToBase64(profileImage);
	} catch {
		photoUrl = '';
	}

	const settings = getLocalStorage('theme');

	const user: User = {
		roles: mappedRoles,
		displayName: `${userData.first_name} ${userData.last_name}`,
		photoURL: photoUrl,
		email: userData.email,
		data: userData,
		settings,
		epp_id: decoded?.epp_id
	};

	// Link the user to Sentry with their email and account info
	Sentry.setUser({ email: user.email });
	Sentry.setContext('account', { epp_id: decoded?.epp_id });

	return user;
}

/**
 * JWT Authentication Provider Component for handling authentication state and operations
 *
 * This component provides authentication functionality using JWT tokens, including:
 * - Auto login with stored token
 * - Sign in with credentials
 * - Sign in with OTP (One-Time Password)
 * - Sign up
 * - Sign out
 * - Password management (forgot/reset)
 * - User updates
 * - Token refreshing
 *
 * The provider manages authentication state and exposes authentication methods through
 * a context that can be consumed by child components.
 *
 * @param props - The component props
 * @param props.ref - Reference forwarded to access provider methods externally
 * @param props.children - Child components that will have access to the auth context
 * @param props.onAuthStateChanged - Optional callback fired when auth state changes
 *
 * @example
 * ```tsx
 * <JwtAuthProvider onAuthStateChanged={(authState) => console.log(authState)}>
 *   <App />
 * </JwtAuthProvider>
 * ```
 */
function JwtAuthProvider(props: FuseAuthProviderComponentProps) {

	const { ref, children, onAuthStateChanged } = props;

	const {
		value: tokenStorageValue,
		setValue: setTokenStorageValue,
		removeValue: removeTokenStorageValue
	} = useLocalStorage<string>('jwt_access_token');

	/**
	 * Fuse Auth Provider State
	 */
	const [authState, setAuthState] = useState<FuseAuthProviderState<User>>({
		authStatus: 'configuring',
		isAuthenticated: false,
		user: null
	});

	/**
	 * Set by signOut({ redirect: false }) - the "soft" sign-out used when another
	 * sign-in is about to run in the same page lifecycle (e.g. panel/universal
	 * login replacing an existing session). It stops the auto-login effect below
	 * from reacting to the resulting isAuthenticated:false transition, since that
	 * effect running here would otherwise race the follow-up sign-in and can
	 * clobber its freshly-authenticated state. Cleared again once a session is
	 * established via setAuth.
	 */
	const suppressAutoLoginRef = useRef(false);

	/**
	 * Monotonic counter bumped by signOut and setAuth. An in-flight auto-login
	 * attempt captures the current value when it starts; if the counter has moved
	 * on by the time its async work resolves, a newer sign-out/sign-in has since
	 * started (e.g. a panel login), so the stale result is discarded instead of
	 * being applied on top of it. suppressAutoLoginRef alone isn't enough here
	 * because it can already have been reset (by a successful newer login) by the
	 * time an older, slower auto-login attempt - one that was already in flight
	 * before suppression was even set - finally resolves.
	 */
	const authGenerationRef = useRef(0);

	/**
	 * Guards interceptFetch so window.fetch only gets monkey-patched once, since
	 * the effect that calls it re-runs on every isAuthenticated:true transition.
	 */
	const fetchInterceptedRef = useRef(false);

	/**
	 * Watch for changes in the auth state
	 * and pass them to the FuseAuthProvider
	 */
	useEffect(() => {
		if (onAuthStateChanged) {
			onAuthStateChanged(authState);
		}
	}, [authState, onAuthStateChanged]);

	/**
	 * Attempt to auto login with the stored token
	 */
	useEffect(() => {
		if (suppressAutoLoginRef.current) {
			return;
		}

		const myGeneration = authGenerationRef.current;

		const attemptAutoLogin = async () => {
			const accessToken = tokenStorageValue;

			if (isTokenValid(accessToken)) {
				try {
					/**
					 * Sign in with the token
					 */
					const response = await authSignInWithToken(accessToken);

					if (!response.ok) {
						throw new Error(`HTTP error! status: ${response.status}`);
					}

					setTokenStorageValue(accessToken);
					setGlobalHeaders({ Authorization: `Bearer ${accessToken}` });

					// Decode the JWT token to extract roles
					const decoded = jwtDecode(accessToken || '') as DecodedToken;

					const newUser = await buildUserProfile(accessToken, decoded);

					return newUser;
				} catch {
					return false;
				}
			}

			return false;
		};

		if (!authState.isAuthenticated) {
			attemptAutoLogin().then((userData) => {
				if (authGenerationRef.current !== myGeneration) {
					// A newer sign-out/sign-in has started since this attempt began -
					// discard this stale result instead of clobbering it.
					return;
				}

				if (userData) {
					setAuthState({
						authStatus: 'authenticated',
						isAuthenticated: true,
						user: userData
					});
				} else {
					removeTokenStorageValue();
					removeGlobalHeaders(['Authorization']);
					setAuthState({
						authStatus: 'unauthenticated',
						isAuthenticated: false,
						user: null
					});
				}
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [authState.isAuthenticated]);

	/**
	 * Helper function to set up authentication state after successful authentication
	 *
	 * This function handles:
	 * - Setting the JWT token in local storage
	 * - Configuring global API headers with the authorization token
	 * - Fetching and processing user profile information
	 * - Setting up the authenticated state with user data
	 *
	 * @param session - The authentication session data containing tokens and expiry information
	 */
	const setAuth = useCallback(async (session: AuthSession) => {
		// Invalidate any auto-login attempt still in flight from before this
		// sign-in started, so it can't resolve later and overwrite this session.
		authGenerationRef.current += 1;

		if (session) {
			setTokenStorageValue(session.token);
			setGlobalHeaders({ Authorization: `Bearer ${session.token}` });
		}

		const decoded = jwtDecode(session.token || '') as DecodedToken;

		const mappedRoles = decoded?.roles?.map(role => role as Role) || [Role.Primary];

		if (mappedRoles.length === 0) {
			throw new Error('no_roles');
		}

		const newUser = await buildUserProfile(session.token, decoded);

		// A session is now established - the auto-login effect no longer needs
		// to stay suppressed on account of any earlier chaining sign-out.
		suppressAutoLoginRef.current = false;

		setAuthState({
			authStatus: 'authenticated',
			isAuthenticated: true,
			user: newUser
		});

	}, [setTokenStorageValue]
	);

	/**
	 * Authenticates a user with username and password credentials
	 *
	 * Sends authentication request to backend, processes the response,
	 * and establishes an authenticated session if successful.
	 *
	 * @param credentials - Object containing user authentication data (username, password)
	 * @returns Promise resolving to the raw API response for additional handling
	 */
	const signIn: JwtAuthContextType['signIn'] = useCallback(
		async (credentials) => {
			const response = await authSignIn(credentials);

			const session = (await response.json()) as AuthSession & {
				detail?: string;
				warning?: string;
			};

			await setAuth(session);

			return response;
		},
		[setAuth]
	);

	/**
	 * Signs in a user using One-Time Password (OTP) authentication
	 *
	 * Handles the two-factor authentication flow with username, password, and OTP code.
	 * When remember_me_duration is provided, sets a cookie to remember the device
	 * to potentially bypass OTP verification on future logins.
	 *
	 * @param credentials - Object containing username, password, OTP code, and optional remember duration
	 * @returns Promise resolving to the raw API response for additional handling
	 */
	const otpSignIn: JwtAuthContextType['otpSignIn'] = useCallback(
		async (credentials) => {
			const apiPayload = {
				username: credentials.username,
				password: credentials.password,
				otp_code: credentials.otp,
				...(credentials.remember_me_duration !== 0 && { remember_me_duration: credentials.remember_me_duration }),
				...(credentials.recovery_code && { recovery_code: credentials.recovery_code }),
				...(credentials.method && { method: credentials.method })
			};

			const response = await authOtpSignIn(apiPayload);

			const session = (await response.json()) as AuthSession & {
				message?: string;
				remember_me_token?: string;
			};

			if (session?.remember_me_token) {
				const expiry = new Date(Date.now() + credentials.remember_me_duration * 24 * 60 * 60 * 1000);
				document.cookie = `mfa-remember=${session.remember_me_token}; expires=${expiry.toUTCString()}; path=/; SameSite=Lax`;
			}

			await setAuth(session);

			return response;
		},
		[setAuth]
	);

	const socialOtpSignIn: JwtAuthContextType['socialOtpSignIn'] = useCallback(
		async (credentials) => {
			const response = await authSocialsOtp({
				'2fa_type': credentials['2fa_type'],
				identifier: credentials.identifier,
				otp_code: credentials.otp,
				...(credentials.remember_me_duration !== 0 && { remember_me_duration: credentials.remember_me_duration }),
				...(credentials.recovery_code && { recovery_code: credentials.recovery_code })
			});

			const session = (await response.json()) as SocialAuthSession;

			if (session?.remember_me_token) {
				const expiry = new Date(Date.now() + credentials.remember_me_duration * 24 * 60 * 60 * 1000);
				document.cookie = `mfa-remember=${session.remember_me_token}; expires=${expiry.toUTCString()}; path=/; SameSite=Lax`;
			}

			await setAuth(transformSocialSession(session));

			return response;

		},
		[setAuth]
	);

	/**
	 * Registers a new user account with the provided information
	 *
	 * Sends registration data to backend API. The account still requires email
	 * verification before it can be used, so this does not authenticate the
	 * user or store a session — the caller navigates to the sign-in page
	 * instead.
	 *
	 * @param data - User registration data including required profile information
	 * @returns Promise resolving to the raw API response for additional handling
	 */
	const signUp: JwtAuthContextType['signUp'] = useCallback(
		async (data) => {
			const response = await authSignUp(data);

			return response;
		},
		[]
	);

	/**
	 * Terminates the current user session and clears authentication state
	 *
	 * Removes JWT token from storage, clears authorization headers,
	 * updates application state to reflect unauthenticated status,
	 * and resets all API cache data to prevent previous user data from being displayed.
	 *
	 * By default this redirects to /sign-in and leaves API calls blocked, since the
	 * page reload is what normally resets that block. Pass `{ redirect: false }` when
	 * signing out is just clearing the way for another sign-in to follow in the same
	 * page lifecycle (e.g. universal/panel login replacing an existing session) -
	 * this re-enables API calls immediately instead of leaving them blocked forever.
	 */
	const signOut: JwtAuthContextType['signOut'] = useCallback((options) => {
		const redirect = options?.redirect ?? true;

		// Invalidate any auto-login attempt still in flight from before this
		// sign-out started, so it can't resolve later and resurrect the old session.
		authGenerationRef.current += 1;

		// Set flag to prevent any new API calls from being initiated
		setSigningOut(true);

		if (!redirect) {
			// A follow-up sign-in is expected in this same page lifecycle - keep the
			// auto-login effect from reacting to the unauthenticated transition below.
			suppressAutoLoginRef.current = true;
		}

		// Remove token and headers first so that the synchronous re-render below
		// (triggered by flushSync) sees no stored token. Otherwise the auto-login
		// effect - which re-runs whenever isAuthenticated flips to false - would
		// read the still-present, soon-to-be-removed token and kick off a second,
		// racing sign-in request (e.g. against a panel login started right after
		// this signOut call), causing duplicate requests and state flapping.
		removeTokenStorageValue();
		removeGlobalHeaders(['Authorization']);

		// Use flushSync to force synchronous state update before any other operations
		flushSync(() => {
			// Clear authentication state first to prevent new API calls
			setAuthState({
				authStatus: 'unauthenticated',
				isAuthenticated: false,
				user: null
			});
		});

		// Clear Sentry user and context
		Sentry.setUser(null);
		Sentry.setContext('account', null);

		// Dispatch resetApiState action to clear all RTK Query cache and abort pending queries
		store.dispatch(apiService.util.resetApiState());

		// Clear config failure tracking to prevent stale failures from triggering error redirect
		clearAllFailures();

		if (redirect) {
			// Redirect after ensuring all cleanup is complete
			window.location.href = '/sign-in';
		} else {
			// No reload is coming, so re-allow API calls for whatever sign-in follows
			setSigningOut(false);
		}
	}, [removeTokenStorageValue]);

	/**
	 * Initiates the password recovery process for a user
	 *
	 * Sends a request to the backend to email a password reset link
	 * to the user's registered email address.
	 *
	 * @param data - Object containing user email and application URL for reset link
	 * @returns Promise resolving to the raw API response for additional handling
	 */
	const forgotPassword: JwtAuthContextType['forgotPassword'] = useCallback(
		async (data) => {
			return authForgotPassword(data);
		}, []
	);

	/**
	 * Completes the password reset process using a reset token
	 *
	 * Allows a user to set a new password after following a reset link
	 * that was sent to their email.
	 *
	 * @param data - Object containing new password, confirmation password, and reset token
	 * @returns Promise resolving to the raw API response for additional handling
	 */
	const resetPassword: JwtAuthContextType['resetPassword'] = useCallback(
		async (data) => {
			const response = await authResetPassword(data);
			return response;
		}, []
	);

	/**
	 * Changes a user's password when they know their current password
	 *
	 * Provides functionality for authenticated users to update their password
	 * by verifying their current password before setting a new one.
	 *
	 * @param data - Object containing old password, new password, and confirmation
	 * @returns Promise resolving to the raw API response for additional handling
	 */
	const resetKnownPassword: JwtAuthContextType['resetKnownPassword'] = useCallback(
		async (data) => {
			const response = await authResetKnownPassword(data);
			return response;
		}, []
	);

	const signInWithSocials: JwtAuthContextType['signInWithSocials'] = useCallback(
		async (data) => {
			setAuthState({
				authStatus: 'loading',
				isAuthenticated: false,
				user: null
			});

			const response = await authSignInSocial(data);

			if (!response.ok) {
				setAuthState({
					authStatus: 'unauthenticated',
					isAuthenticated: false,
					user: null
				});
			}

			const session = (await response.json()) as SocialAuthSession;

			setAuth(transformSocialSession(session));

			return response;

		},
		[setAuth]
	);

	const signUpWithSocials: JwtAuthContextType['signUpWithSocials'] = useCallback(
		async (data) => {
			setAuthState({
				authStatus: 'loading',
				isAuthenticated: false,
				user: null
			});

			const response = await authSignUpSocial(data);

			if (!response.ok) {
				setAuthState({
					authStatus: 'unauthenticated',
					isAuthenticated: false,
					user: null
				});
			}

			const session = (await response.json()) as SocialAuthSession;

			setAuth(transformSocialSession(session));

			return response;

		},
		[setAuth]
	);

	const panelLogin: JwtAuthContextType['panelLogin'] = useCallback(
		async (flag) => {
			setAuthState({
				authStatus: 'loading',
				isAuthenticated: false,
				user: null
			});

			const response = await apiFetch(`/portal/auth-jwt-panel/?${new URLSearchParams({ tag: flag })}`, {
				method: 'GET',
				headers: {
					'Content-Type': 'application/json'
				}
			});

			if (!response.ok) {
				setAuthState({
					authStatus: 'unauthenticated',
					isAuthenticated: false,
					user: null
				});
			}

			const session = (await response.json()) as AuthSession;

			await setAuth(session);

			return response;
		},
		[setAuth]
	);

	/**
	 * Updates user information in the system
	 *
	 * Currently a placeholder function that will be implemented to send
	 * updated user profile data to the backend.
	 *
	 * @param _user - User data to be updated
	 * @returns Promise resolving to null (will be updated when implemented)
	 */
	const updateUser: JwtAuthContextType['updateUser'] = useCallback(async (_user) => {
		return null;
	}, []);

	/**
	 * Fetches a new access token using the refresh token
	 *
	 * Used to maintain user session without requiring re-authentication
	 * when the current JWT token expires.
	 *
	 * @returns Promise resolving to the refresh token API response
	 * @throws Error if the refresh token request fails
	 */
	const refreshToken: JwtAuthContextType['refreshToken'] = useCallback(async () => {
		const response = await authRefreshToken();

		if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

		return response;
	}, []);

	/**
	 * Auth Context Value
	 */
	const authContextValue = useMemo(
		() =>
			({
				...authState,
				signIn,
				otpSignIn,
				signUp,
				signOut,
				updateUser,
				refreshToken,
				forgotPassword,
				resetPassword,
				resetKnownPassword,
				signInWithSocials,
				signUpWithSocials,
				socialOtpSignIn,
				panelLogin
			}) as JwtAuthContextType,
		[
			authState, signIn, otpSignIn,
			signUp, signOut, updateUser,
			refreshToken, forgotPassword, resetPassword,
			resetKnownPassword, signInWithSocials, signUpWithSocials,
			socialOtpSignIn, panelLogin
		]
	);

	/**
	 * Expose methods to the FuseAuthProvider
	 */
	useImperativeHandle(ref, () => ({
		signOut,
		updateUser
	}));

	/**
	 * Intercepts all fetch requests to handle token refreshing and authentication errors
	 *
	 * This function monkey-patches the global fetch API to:
	 * - Extract and apply new access tokens from response headers
	 * - Handle 401 Unauthorized responses by signing out the user
	 *
	 * @returns void
	 */
	const interceptFetch = useCallback(() => {
		if (fetchInterceptedRef.current) {
			// Already wrapped - re-running this (e.g. on every isAuthenticated:true
			// transition) would wrap the already-wrapped fetch again, stacking
			// duplicate 401 handlers on top of each other.
			return;
		}

		fetchInterceptedRef.current = true;

		const { fetch: originalFetch } = window;

		window.fetch = async (...args) => {
			const [resource, config] = args;
			const response = await originalFetch(resource, config);
			// const newAccessToken = response.headers.get('New-Access-Token');

			// if (newAccessToken) {
			// 	setGlobalHeaders({ Authorization: `Bearer ${newAccessToken}` });
			// 	setTokenStorageValue(newAccessToken);
			// }

			if (response.status === 401) {
				if (suppressAutoLoginRef.current) {
					// A chaining sign-in (e.g. universal/panel login) is in progress -
					// the auth header was just cleared on purpose and some unrelated,
					// already in-flight request is 401ing as a transient side effect
					// of that, not because the session is actually invalid. Don't let
					// that hard-sign-out and abort the login that's about to replace it.
					return response;
				}

				signOut();
				return;
			}

			return response;
		};
	}, [signOut]);

	useEffect(() => {
		if (authState.isAuthenticated) {
			// Note: a universal/panel login link (?flag= or ?tag=) landing on
			// /sign-in while already authenticated is handled by JwtSignInForm's
			// own effect (soft sign-out + panelLogin), not here. FuseAuthProvider
			// gates its children behind auto-login resolving first, so that
			// effect can only run *after* this one - a hard signOut() here would
			// always win the race and discard the flag before JwtSignInForm ever
			// gets a chance to process it.
			interceptFetch();
		}
	}, [authState.isAuthenticated, interceptFetch]);

	return <JwtAuthContext value={authContextValue}>{children}</JwtAuthContext>;
}

export default JwtAuthProvider;
